import Collapse from '@mui/material/Collapse';
import { type Currency, FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { InfoIcon, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useState } from 'react';
import { useField } from 'react-final-form';

import GreenArrow from '../../assets/icons/green-arrow.svg';
import type { Rates } from '../../types/Cashier';
import { convertAmountByRate, getConversionRate } from '../../utils/cashier';
import Button from '../Button/Button';
import ConversionCurrency from '../ConversionCurrency/ConversionCurrency';
import ExchangeRateFormula from '../ExchangeRateFormula/ExchangeRateFormula';

import {
  TXTWCL_Exchange_rates,
  TXTWCL_WeXll_convert_it_to,
  TXTWCL_We_operate_worldwideX_so_havin_ELL,
  TXTWCL_Why_is_my_deposit_being_conver_ELL,
  TXTWCL_Your_deposit_limit_will_be_set_ELL
} from './ConversionInformation.messages.json';
import {
  active,
  amountConversion,
  arrow,
  collapseContainer,
  container,
  conversion,
  conversionParagraph,
  expandButton,
  headline,
  tooltipIcon
} from './ConversionInformation.scss';

interface Props {
  rate: number;
  balanceCurrency: Currency;
  fieldName: string;
  rates: Rates;
}

const initialAmount = 0;
const collapseClasses: Record<string, string> = {
  root: collapseContainer
};

const ConversionInformation: React.FC<Props> = ({ rate, balanceCurrency, fieldName, rates }) => {
  const [expand, setExpand] = useState(false);
  const {
    input: {
      value: { amount = initialAmount, currency: selectedCurrency }
    }
  } = useField(fieldName, { subscription: { value: true } });
  const isSwcHideFxInfoEnabled = useFeature(FeatureFlag.SwcHideFxInfo);

  const buttonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setExpand(!expand);
  };

  const conversionRate = getConversionRate(rates, selectedCurrency);
  const convertedAmount = convertAmountByRate(amount, rate, balanceCurrency, false);

  return (
    <div className={container}>
      <div className={headline}>
        <Translation {...TXTWCL_WeXll_convert_it_to} />
      </div>
      <ConversionCurrency
        amount={convertedAmount}
        balanceCurrency={balanceCurrency}
        className={amountConversion}
      />
      <div className={conversion}>
        <ExchangeRateFormula
          maximumFractionDigits={4}
          minimumFractionDigits={4}
          sourceAmountInCents={100}
          sourceCurrency={selectedCurrency}
          targetCurrency={balanceCurrency}
          inverted={false}
          rate={conversionRate}
        />
      </div>
      <br />
      {!isSwcHideFxInfoEnabled && (
        <>
          <Button
            firstVariant="dropdown"
            translation={TXTWCL_Why_is_my_deposit_being_conver_ELL}
            endIcon={<GreenArrow className={classNames(arrow, { [active]: expand })} />}
            className={expandButton}
            onClick={buttonClick}
            firstButtonIcon={<InfoIcon className={classNames(tooltipIcon, { [active]: expand })} />}
          />
          <Collapse classes={collapseClasses} in={expand} data-testid="conversion-information">
            <p className={conversionParagraph}>
              <Translation
                {...TXTWCL_We_operate_worldwideX_so_havin_ELL}
                values={{ 0: balanceCurrency }}
              />
            </p>
            <Translation tagName="b" {...TXTWCL_Exchange_rates} />
            <br />
            <Translation {...TXTWCL_Your_deposit_limit_will_be_set_ELL} />
          </Collapse>
        </>
      )}
    </div>
  );
};

export default ConversionInformation;
